import React from 'react'

import {
    Avatar,
    HStack,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    VStack,
    Text,
    ModalBody,
    Flex,
    Link,
    useToast,
    Box,
    Button,
} from '@chakra-ui/react'

import ApplyV2Tooltip from '@core/components/ApplyDialogV2/ApplyV2Tooltip'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import { prefixes } from '@core/utils/helpers/prefixes'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import type { ContactV2 } from '@page/post-v2/types'

import './PostPageV2ContactPersonModal.scss'
import { useTranslation } from 'react-i18next'

const baseClass = 'PostPageV2ContactPerson__modal'

interface PostPageV2ContactPersonModalProps {
    contactPerson: ContactV2 | null
    onClose: () => void
}

function PostPageV2ContactPersonModal({ contactPerson, onClose }: PostPageV2ContactPersonModalProps) {
    const { getTitle } = useApplyV2NavTranslations()

    function formatURL(url: string) {
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            return `https://${url}`
        }
        return url
    }

    const isPhonesTheSame =
        contactPerson?.mobilePhone?.number === contactPerson?.workPhone?.number &&
        contactPerson?.mobilePhone?.prefix === contactPerson?.workPhone?.prefix

    return (
        <Modal isOpen={!!contactPerson} isCentered size={'lg'} onClose={onClose} motionPreset="slideInBottom">
            <ModalOverlay className={`${baseClass}__overlay`} />
            <ModalContent
                className={`${baseClass}__content`}
                containerProps={{
                    padding: '16px',
                }}
                maxWidth={'480px'}
            >
                <ModalHeader className={`${baseClass}__header`} px={'24px'} pt={'16px'}>
                    <HStack gap={'8px'} alignItems={'center'}>
                        <Avatar w={'80px'} h={'80px'} src={contactPerson?.image} />
                        <VStack gap={'2px'} alignItems={'start'}>
                            <Text fontSize={'14px'} fontWeight={'600'} lineHeight={'20px'} color={'#0C0C0D'}>
                                {decodeHtmlEntitiesRecursive(contactPerson?.firstName)}{' '}
                                {decodeHtmlEntitiesRecursive(contactPerson?.lastName)}
                            </Text>
                            <Text fontSize={'12px'} lineHeight={'16px'} fontWeight={'500'} color={'#717175'}>
                                {' '}
                                {decodeHtmlEntitiesRecursive(contactPerson?.title)}
                            </Text>
                        </VStack>
                    </HStack>
                    <ApplyV2Tooltip
                        label={getTitle('181', 'Close')}
                        placement="top"
                        style={{
                            position: 'absolute',
                            right: '20px',
                            top: '20px',
                            // width: '32px',
                        }}
                    >
                        <ModalCloseButton
                            className={`${baseClass}__close-button`}
                            color={'#77777C'}
                            borderRadius={9999}
                            backgroundColor={'#F5F5F5'}
                            position={'relative'}
                            _hover={{
                                bg: '#e3e3e3',
                            }}
                            top={{
                                base: '0',
                                // sm: '30px',
                            }}
                            right={{
                                base: '0',
                                // sm: '40px',
                            }}
                        />
                    </ApplyV2Tooltip>
                    {/*</Tooltip>*/}
                </ModalHeader>
                <ModalBody
                    className={`${baseClass}__body`}
                    // px={{
                    //     base: '20px',
                    //     sm: '48px',
                    // }}
                    // pb={'0'}
                    px={'24px'}
                    pb={'24px'}
                    pt={'8px'}
                >
                    {contactPerson?.linkedin && (
                        <Link
                            display={'inline-block'}
                            href={formatURL(contactPerson?.linkedin)}
                            target={'_blank'}
                            aria-label={'LinkedIn'}
                            _hover={{
                                textDecoration: 'none',
                            }}
                            _visited={{
                                textDecoration: 'none',
                                color: '#0C0C0D',
                            }}
                            mb={'24px'}
                        >
                            <Flex
                                gap={'8px'}
                                alignItems={'center'}
                                padding={'0px 16px'}
                                borderRadius={'8px'}
                                border={'1px solid #EAEAEB'}
                                height={'36px'}
                                transition={'background-color 0.2s ease'}
                                _hover={{
                                    backgroundColor: '#F5F5F5',
                                }}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.1878 1.33594C13.5807 1.33594 13.9575 1.49202 14.2354 1.76985C14.5132 2.04768 14.6693 2.42451 14.6693 2.81742V13.1878C14.6693 13.5807 14.5132 13.9575 14.2354 14.2354C13.9575 14.5132 13.5807 14.6693 13.1878 14.6693H2.81742C2.42451 14.6693 2.04768 14.5132 1.76985 14.2354C1.49202 13.9575 1.33594 13.5807 1.33594 13.1878V2.81742C1.33594 2.42451 1.49202 2.04768 1.76985 1.76985C2.04768 1.49202 2.42451 1.33594 2.81742 1.33594H13.1878ZM12.8174 12.8174V8.89149C12.8174 8.25104 12.563 7.63683 12.1101 7.18396C11.6573 6.7311 11.0431 6.47668 10.4026 6.47668C9.77297 6.47668 9.03964 6.86186 8.68409 7.43964V6.61742H6.61742V12.8174H8.68409V9.16557C8.68409 8.5952 9.14334 8.12853 9.71372 8.12853C9.98875 8.12853 10.2525 8.23779 10.447 8.43227C10.6415 8.62675 10.7508 8.89053 10.7508 9.16557V12.8174H12.8174ZM4.21001 5.45446C4.54006 5.45446 4.85659 5.32335 5.08997 5.08997C5.32335 4.85659 5.45446 4.54006 5.45446 4.21001C5.45446 3.52112 4.8989 2.95816 4.21001 2.95816C3.878 2.95816 3.55959 3.09005 3.32482 3.32482C3.09005 3.55959 2.95816 3.878 2.95816 4.21001C2.95816 4.8989 3.52112 5.45446 4.21001 5.45446ZM5.23964 12.8174V6.61742H3.18779V12.8174H5.23964Z"
                                        fill="#0B66C2"
                                    />
                                </svg>
                                {/*<Text color={'#0C0C0D'} fontSize={'13px'} lineHeight={'18px'} fontWeight={'500'}>*/}
                                {/*    LinkedIn*/}
                                {/*</Text>*/}
                            </Flex>
                        </Link>
                    )}
                    <VStack gap={'16px'} alignItems={'stretch'}>
                        {contactPerson?.email && (
                            <PersonContactRow
                                type={'mailto'}
                                phonePrefix={contactPerson?.mobilePhone?.prefix}
                                value={contactPerson?.email}
                                isMobile
                            />
                        )}
                        {contactPerson?.mobilePhone && (
                            <PersonContactRow
                                type={'tel'}
                                phonePrefix={contactPerson?.mobilePhone?.prefix}
                                value={contactPerson?.mobilePhone?.number}
                                isMobile
                            />
                        )}
                        {!isPhonesTheSame && contactPerson?.workPhone && (
                            <PersonContactRow
                                type={'tel'}
                                phonePrefix={contactPerson?.workPhone?.prefix}
                                value={contactPerson?.workPhone?.number}
                                // isMobile={false}
                            />
                        )}
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

export default PostPageV2ContactPersonModal

function PersonContactRow({
    icon,
    type,
    value,
    phonePrefix,
    isMobile = false,
}: {
    icon?: React.ReactNode
    type: 'tel' | 'mailto'
    value: string | number
    phonePrefix?: string | null
    isMobile?: boolean
}) {
    const toast = useToast()
    const { t } = useTranslation()

    const rawPhone = `${phonePrefix || ''} ${value}`
    const cleanPhoneForLogic = rawPhone.replace(/[^\d+]/g, '')
    const findPrefix = () => {
        return prefixes.find(({ prefix }) => cleanPhoneForLogic.startsWith(prefix))
    }

    const displayValue = type === 'tel' ? rawPhone : value
    const actionValue = type === 'tel' ? cleanPhoneForLogic : value
    const prefixItem = type === 'tel' ? findPrefix() : null
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(`${actionValue}`)
            toast({
                title: t('6272'),
                description: displayValue,
                status: 'success',
                duration: 2000,
                isClosable: true,
            })
        } catch (error) {
            console.log('Copy error:', error)
            toast({
                title: t('3875'),
                description: displayValue,
                status: 'error',
                duration: 2000,
                isClosable: true,
            })
        }
    }

    return (
        <Flex className={baseClass} gap={'8px'} alignItems={'center'}>
            <Flex
                w={'44px'}
                h={'44px'}
                borderRadius={'50%'}
                backgroundColor={'#FAFAFA'}
                alignItems={'center'}
                justifyContent={'center'}
                gap={'12px'}
            >
                {icon}
                {!icon && type === 'tel' && !isMobile && (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.7447 3.1148C6.57946 2.74301 6.18893 2.52462 5.78566 2.5785L3.21057 2.92252C2.792 2.97844 2.48876 3.34973 2.51757 3.77103C3.02066 11.1289 8.8778 16.986 16.2356 17.4891C16.6569 17.5179 17.0282 17.2147 17.0841 16.7961L17.4282 14.221C17.482 13.8177 17.2637 13.4272 16.8919 13.262L14.2569 12.0909C14.0154 11.9836 13.7394 11.9857 13.4996 12.0967L12.4697 12.5734C11.55 12.9992 10.4623 12.8058 9.74563 12.0892L7.91746 10.261C7.20081 9.54437 7.00746 8.45668 7.43323 7.53696L7.90997 6.5071C8.02099 6.26728 8.0231 5.99121 7.91577 5.74972L6.7447 3.1148ZM5.58703 1.09171C6.65021 0.94967 7.67978 1.52542 8.11542 2.50559L9.28649 5.14051C9.56945 5.77717 9.56388 6.50498 9.27119 7.13724L8.79445 8.1671C8.63295 8.51596 8.70629 8.92853 8.97812 9.20036L10.8063 11.0285C11.0781 11.3004 11.4907 11.3737 11.8396 11.2122L12.8694 10.7355C13.5017 10.4428 14.2295 10.4372 14.8661 10.7202L17.5011 11.8912C18.4812 12.3269 19.057 13.3565 18.9149 14.4196L18.5709 16.9947C18.4103 18.1972 17.3436 19.0684 16.1333 18.9856C8.0277 18.4314 1.57529 11.979 1.02106 3.87335C0.938305 2.66302 1.80947 1.59637 3.01194 1.43573L5.58703 1.09171Z"
                            fill="#77777C"
                        />
                    </svg>
                )}
                {!icon && type === 'tel' && isMobile && (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.66667 0.914062C5.33198 0.914062 4.25 1.99604 4.25 3.33073V16.6641C4.25 17.9987 5.33198 19.0807 6.66667 19.0807H13.3333C14.668 19.0807 15.75 17.9987 15.75 16.6641V3.33073C15.75 1.99604 14.668 0.914062 13.3333 0.914062H6.66667ZM5.75 3.33073C5.75 2.82447 6.16041 2.41406 6.66667 2.41406H13.3333C13.8396 2.41406 14.25 2.82447 14.25 3.33073V16.6641C14.25 17.1703 13.8396 17.5807 13.3333 17.5807H6.66667C6.16041 17.5807 5.75 17.1703 5.75 16.6641V3.33073ZM9.99992 15.8306C10.6903 15.8306 11.2499 15.271 11.2499 14.5806C11.2499 13.8903 10.6903 13.3306 9.99992 13.3306C9.30956 13.3306 8.74992 13.8903 8.74992 14.5806C8.74992 15.271 9.30956 15.8306 9.99992 15.8306Z"
                            fill="#77777C"
                        />
                    </svg>
                )}
                {!icon && type === 'mailto' && (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M2.41406 5.0026C2.41406 4.49634 2.82447 4.08594 3.33073 4.08594H16.6641C17.1703 4.08594 17.5807 4.49634 17.5807 5.0026V5.58068C17.558 5.59202 17.5355 5.60461 17.5136 5.61849L10.0419 10.3374C10.0147 10.3546 9.98008 10.3546 9.9529 10.3374L2.48122 5.61849C2.45925 5.60461 2.43684 5.59202 2.41406 5.58068V5.0026ZM2.41406 7.35019V15.0026C2.41406 15.5089 2.82447 15.9193 3.33073 15.9193H9.9974H16.6641C17.1703 15.9193 17.5807 15.5089 17.5807 15.0026V7.35019L10.8429 11.6057C10.3264 11.9319 9.66837 11.9319 9.15191 11.6057L2.41406 7.35019ZM3.33073 2.58594C1.99604 2.58594 0.914062 3.66792 0.914062 5.0026V15.0026C0.914062 16.3373 1.99604 17.4193 3.33073 17.4193H9.9974H16.6641C17.9987 17.4193 19.0807 16.3373 19.0807 15.0026V5.0026C19.0807 3.66792 17.9987 2.58594 16.6641 2.58594H3.33073Z"
                            fill="#77777C"
                        />
                    </svg>
                )}
            </Flex>
            <Flex flexDirection={'column'} alignItems={'start'} gap={'4px'} flexGrow={1}>
                <Text fontSize={'12px'} lineHeight={'16px'} fontWeight={'500'}>
                    {type === 'mailto' && t('92', 'Email')}
                    {type === 'tel' && isMobile && t('2972', 'Mobile')}
                    {type === 'tel' && !isMobile && t('2', 'Phone')}
                </Text>
                <Link
                    href={`${type}:${actionValue}`}
                    fontSize={'14px'}
                    lineHeight={'20px'}
                    alignItems={'center'}
                    gap={'8px'}
                    fontWeight={'500'}
                    color={'#0C0C0D'}
                    _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                    wordBreak={'break-word'}
                    whiteSpace={'normal'}
                    overflowWrap={'break-word'}
                >
                    {displayValue}
                </Link>
            </Flex>
            <ApplyV2Tooltip label={t('5708', 'Copy')} openDelay={1}>
                <button onClick={handleCopy} className={`${baseClass}__copy`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M5.59844 2.66771C5.59844 2.2627 5.92676 1.93438 6.33177 1.93438H9.46094V4.23269C9.46094 5.30044 10.3265 6.16603 11.3943 6.16603H13.3984V10.157C13.3984 10.562 13.0701 10.8903 12.6651 10.8903H6.33177C5.92676 10.8903 5.59844 10.562 5.59844 10.157V2.66771ZM10.6609 4.23269V2.29874L13.1889 4.87823C13.2158 4.90571 13.2403 4.93508 13.2624 4.96603H11.3943C10.9893 4.96603 10.6609 4.6377 10.6609 4.23269ZM6.33177 0.734375C5.26402 0.734375 4.39844 1.59996 4.39844 2.66771V10.157C4.39844 11.2248 5.26402 12.0903 6.33177 12.0903H12.6651C13.7329 12.0903 14.5984 11.2248 14.5984 10.157V5.56603V5.39152C14.5984 4.88553 14.4001 4.3997 14.0459 4.03832L11.3309 1.26795C10.9965 0.926682 10.5388 0.734375 10.0609 0.734375H6.33177ZM2.59844 5.84512C2.59844 5.44011 2.92676 5.11178 3.33177 5.11178V3.91178C2.26402 3.91178 1.39844 4.77737 1.39844 5.84512V13.3344C1.39844 14.4022 2.26402 15.2677 3.33177 15.2677H9.99844C10.8821 15.2677 11.5984 14.5514 11.5984 13.6678V13.0677H10.3984V13.6678C10.3984 13.8887 10.2194 14.0677 9.99844 14.0677H3.33177C2.92676 14.0677 2.59844 13.7394 2.59844 13.3344V5.84512Z"
                            fill="#77777C"
                        />
                    </svg>
                </button>
            </ApplyV2Tooltip>
        </Flex>
    )
}
